import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, Clock2, Play, Pause, DoorOpen, NotebookPen, CircleAlert, Loader2, Loader, Coffee, RefreshCw } from "lucide-react";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
    Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter,
    SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb";
import { useAuth } from "@/context/AuthContext";
import { useServerTime } from "@/hooks/useServerTime";
import { createWorkSession } from "@/lib/collections/workSession/createWorkSession";
import { updateWorkSessionEnd } from "@/lib/collections/workSession/updateWorkSession";
import { endBreak, getActiveBreak, getFinishedBreaks, startBreak } from "@/lib/collections/workBreaks/breaks";
import { databases } from "@/lib/appwrite";
import { getActiveWorkSessionForToday } from "@/lib/collections/workSession/getStatusWorkSession";
import { createSessionNote } from "@/lib/collections/WorkSessionNotes/createNotes";
import { toast } from "react-toastify";
import { checkIfSessionExistsToday } from "@/lib/collections/workSession/checkIfSessionExistsToday";
import { useRealtimeCollection } from "@/hooks/realtime/useRealtimeCollection";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import Feedback from "./feedback";
import PhraseOfTheDay from "./phrase";
import { useOnlineStatus } from "@/hooks/offline/useOnlineStatus";
import DashboardEvents from "./DashboardEvents";


const getColorByInitial = (name: string | undefined | null) => {
    if (!name || name.length === 0) return "#000";
    const initial = name[0].toLowerCase();
    const colorMap: Record<string, string> = {
        a: "#FF6B6B", b: "#FFD93D", c: "#A0522D", d: "#4ECDC4", e: "#FF9F1C", f: "#9D4EDD", g: "#F65164", h: "#06D6A0", i: "#8338EC", j: "#3A86FF", k: "#FFB703", l: "#FB5607",
        m: "#8AC926", n: "#1982C4", o: "#6A4C93", p: "#CC66DA", q: "#8338EC", r: "#FF2800", s: "#2A9D8F", t: "#264653", u: "#E76F51", v: "#1D3557", w: "#F72585", x: "#C77DFF",
        y: "#3A0CA3", z: "#D00000",
    };
    return colorMap[initial] || "#5775FF";
};


const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const Dashboard = () => {
    const { user } = useAuth();
    const nameColor = useMemo(() => getColorByInitial(user?.name), [user?.name]);
    const { getCurrentEcuadorTimeString } = useServerTime();

    const [ecuadorTime, setEcuadorTime] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isBreakLoading, setIsBreakLoading] = useState(false);
    const [activeSession, setActiveSession] = useState<any | null>(null);
    const [activeBreak, setActiveBreak] = useState<any | null>(null);
    const [breakElapsedTime, setBreakElapsedTime] = useState<number>(0);
    const [breakLimitReached, setBreakLimitReached] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [isSendingNote, setIsSendingNote] = useState(false);
    const [hasSessionToday, setHasSessionToday] = useState(false);

    // Estado de conexión centralizado para controlar acciones del timbrador
    const isOnline = useOnlineStatus();

    //  Estados para controlar la carga inicial
    const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
    const [isInitialSessionCheck, setIsInitialSessionCheck] = useState(true);

    // Referencias para el timer del break
    const breakIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const breakStartTimeRef = useRef<Date | null>(null);
    const breakInFlightRef = useRef(false);

    // Configuración para realtime de breaks
    const dbId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
    const breaksCollectionId = import.meta.env.VITE_WORK_BREAKS_COLLECTION_ID;
    const sessionsCollectionId = import.meta.env.VITE_WORK_SESSIONS_COLLECTION_ID;

    // Fetchers para realtime
    const breaksCollectionIds = useMemo(() => [breaksCollectionId], [breaksCollectionId]);
    const sessionsCollectionIds = useMemo(() => [sessionsCollectionId], [sessionsCollectionId]);

    const filterEvents = useMemo(() => ['create', 'update', 'delete'], []);

    // Fetcher para breaks del usuario actual y sesion activa
    const fetchBreaks = useCallback(async () => {
        if (!user?.$id || !activeSession?.$id) return [];
        try {
            const [activeBreakResponse, finishedBreaksResponse] = await Promise.all([
                getActiveBreak(user.$id, activeSession.$id),
                getFinishedBreaks(user.$id, activeSession.$id)
            ]);

            return [
                ...activeBreakResponse.documents,
                ...finishedBreaksResponse.documents
            ];
        } catch (error) {
            console.error("Error fetching breaks:", error);
            return [];
        }
    }, [user?.$id, activeSession?.$id]);

    // Fetcher para sesiones del usuario actual
    const fetchSessions = useCallback(async () => {
        if (!user?.$id) return [];
        try {
            const session = await getActiveWorkSessionForToday(user.$id);
            return session ? [session] : [];
        } catch (error) {
            console.error("Error fetching sessions:", error);
            return [];
        }
    }, [user?.$id]);

    // Hooks de realtime
    const [realtimeBreaks, isBreaksLoading] = useRealtimeCollection(
        dbId,
        breaksCollectionIds,
        fetchBreaks,
        filterEvents
    );

    const [realtimeSessions, isSessionsLoading] = useRealtimeCollection(
        dbId,
        sessionsCollectionIds,
        fetchSessions,
        filterEvents
    );


    useEffect(() => {
        // Solo considera que termino la carga inicial cuando ambos hooks hayan terminado
        // y hayamos hecho la verificación inicial de sesión
        if (!isBreaksLoading && !isSessionsLoading && !isInitialSessionCheck) {
            // Pequeño delay para evitar parpadeos
            const timer = setTimeout(() => {
                setIsInitialDataLoading(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isBreaksLoading, isSessionsLoading, isInitialSessionCheck]);

    // Procesar datos de realtime para extraer breaks
    useEffect(() => {
        if (!realtimeBreaks.length) {
            setActiveBreak(null);
            return;
        }

        const activeBreakFromRealtime = realtimeBreaks.find((br: any) => br.isActive === true);

        if (activeBreakFromRealtime && activeBreakFromRealtime.$id !== activeBreak?.$id) {
            setActiveBreak(activeBreakFromRealtime);

            if (activeBreakFromRealtime.startTime) {
                breakStartTimeRef.current = new Date(activeBreakFromRealtime.startTime);
                startBreakTimer();
            }
        } else if (!activeBreakFromRealtime && activeBreak) {
            setActiveBreak(null);
            breakStartTimeRef.current = null;
            setBreakElapsedTime(0);
            stopBreakTimer();
        }
    }, [realtimeBreaks, activeBreak?.$id]);

    // Procesar datos de realtime para extraer sesiones
    useEffect(() => {
        if (realtimeSessions.length > 0) {
            const session = realtimeSessions[0];

            const sessionChanged = !activeSession ||
                activeSession.$id !== session.$id ||
                (activeSession as any).status !== (session as any).status ||
                activeSession.$updatedAt !== session.$updatedAt;

            if (sessionChanged) {
                setActiveSession(session);
                setHasSessionToday(true);

                const limitReached = (session as any).totalBreakTime >= 60 || (session as any).breakLimitReached;
                setBreakLimitReached(limitReached);
            }
        } else if (activeSession) {
            setActiveSession(null);
        }
    }, [realtimeSessions, activeSession]);

    // Verificar si hay sesión hoy al inicializar
    useEffect(() => {
        if (!user) return;

        async function checkSessionToday() {
            try {
                const sessionExists = await checkIfSessionExistsToday(user!.$id);
                setHasSessionToday(sessionExists);
            } catch (error) {
                console.error("Error checking session today:", error);
            } finally {
                //  NUEVO: Marcar que terminó la verificación inicial
                setIsInitialSessionCheck(false);
            }
        }

        checkSessionToday();
    }, [user]);

    // Actualizar hora en tiempo real
    useEffect(() => {
        const updateClock = () => {
            const timeStr = getCurrentEcuadorTimeString();
            if (timeStr) setEcuadorTime(timeStr);
        };

        updateClock();
        const clockInterval = setInterval(updateClock, 1000);
        return () => clearInterval(clockInterval);
    }, [getCurrentEcuadorTimeString]);

    // Función para iniciar el contador del break
    const startBreakTimer = () => {
        if (breakIntervalRef.current) {
            clearInterval(breakIntervalRef.current);
        }

        breakIntervalRef.current = setInterval(() => {
            if (!breakStartTimeRef.current) return;

            const now = new Date();
            const elapsed = now.getTime() - breakStartTimeRef.current.getTime();
            setBreakElapsedTime(elapsed);
        }, 1000);
    };

    const stopBreakTimer = () => {
        if (breakIntervalRef.current) {
            clearInterval(breakIntervalRef.current);
            breakIntervalRef.current = null;
        }
    };

    // Cleanup del timer al desmontar
    useEffect(() => {
        return () => {
            stopBreakTimer();
        };
    }, []);

    const getLocation = async (): Promise<[number, number]> => {
        try {
            const perm = await navigator.permissions.query({ name: 'geolocation' });
            if (perm.state === 'denied') {
                toast.error('No diste permiso de ubicación. Activa el GPS para continuar.');
                throw new Error('Permiso de geolocalización denegado');
            }
        } catch {
            // continuar si no se puede verificar el permiso
        }

        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                toast.error('Tu navegador no soporta geolocalización.');
                return reject(new Error('Geolocalización no soportada'));
            }

            navigator.geolocation.getCurrentPosition(
                pos => resolve([pos.coords.latitude, pos.coords.longitude]),
                err => {
                    if (err.code === err.PERMISSION_DENIED) {
                        toast.error('Debes permitir la ubicación para usar esta función.');
                    } else {
                        toast.error('Error al obtener tu ubicación.');
                    }
                    reject(err);
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        });
    };

    const handleStartWork = async () => {
        if (!user || isLoading) return;

        if (!isOnline) {
            toast.error("Sin conexión a internet. No se puede iniciar la jornada.");
            return;
        }

        setIsLoading(true);

        try {
            const now = new Date();
            let location;

            try {
                location = await getLocation();
            } catch (locationError) {
                setIsLoading(false);
                return;
            }

            await createWorkSession(user.$id, now, location);

            setBreakLimitReached(false);
            setBreakElapsedTime(0);

            toast.success(
                <div className="flex flex-col justify-center text-center">
                    <span className="font-outfit text-lg text-center">¡Here we Go 😎!</span>
                    <span className="text-sm font-outfit text-gray-200 text-center">Tu asistencia ha sido registrada.</span>
                </div>
            );

            setHasSessionToday(true);

        } catch (error) {
            if (error instanceof Error) {
                toast.error("Ya registraste tu jornada hoy. Espera hasta mañana para iniciar otra.");
                setHasSessionToday(true);
            } else {
                console.error("Error iniciando jornada", error);
                toast.error("Hubo un error al registrar la jornada.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEndWork = async () => {
        if (!activeSession || isLoading) return;

        if (!isOnline) {
            toast.error("Sin conexión a internet. No se puede finalizar la jornada.");
            return;
        }

        setIsLoading(true);
        try {
            const now = new Date();
            const location = await getLocation();

            await updateWorkSessionEnd(activeSession.$id, now, location);

            if (activeBreak) {
                await handleEndBreak();
            }


            setHasSessionToday(true);
            stopBreakTimer();

        } catch (error) {
            console.error("Error finalizando jornada", error);
            toast.error("Error al finalizar la jornada.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartBreak = async () => {
        if (breakInFlightRef.current || !user || !activeSession || breakLimitReached || isBreakLoading) return;

        if (!isOnline) {
            toast.error("Sin conexión a internet. No se puede iniciar el break.");
            return;
        }

        breakInFlightRef.current = true;
        setIsBreakLoading(true);
        const clickTime = new Date();

        try {
            let locationArray: [string, string];
            try {
                const [lat, lng] = await getLocation();
                locationArray = [lat.toString(), lng.toString()];
            } catch (locationError) {
                toast.error('No se pudo obtener tu ubicación. Activa el GPS e intenta de nuevo.');
                return;
            }

            breakStartTimeRef.current = clickTime;
            setBreakElapsedTime(0);
            startBreakTimer();

            const optimisticBreak = {
                $id: 'optimistic',
                startTime: clickTime.toISOString(),
                location: locationArray,
                isActive: true
            };
            setActiveBreak(optimisticBreak);

            await Promise.all([
                startBreak(user.$id, activeSession.$id, clickTime, locationArray),
                databases.updateDocument(
                    dbId,
                    sessionsCollectionId,
                    activeSession.$id,
                    {
                        status: 'break',
                        updatedAt: clickTime.toISOString(),
                    }
                ),
            ]);

            toast.success('¡Break iniciado! ☕');

        } catch (err) {
            console.error('Error iniciando break', err);
            stopBreakTimer();
            breakStartTimeRef.current = null;
            setActiveBreak(null);
            toast.error('No se pudo iniciar el break.');
        } finally {
            breakInFlightRef.current = false;
            setIsBreakLoading(false);
        }
    };


    const handleEndBreak = async () => {
        if (breakInFlightRef.current || !activeBreak || !activeSession || isBreakLoading) return;

        if (!isOnline) {
            toast.error("Sin conexión a internet. No se puede finalizar el break.");
            return;
        }

        breakInFlightRef.current = true;
        setIsBreakLoading(true);
        try {
            const userClickTime = new Date();

            if (!activeBreak.startTime) {
                console.warn("StartTime del break no está definido.");
                return;
            }

            const startTime = new Date(activeBreak.startTime);
            const duration = (userClickTime.getTime() - startTime.getTime()) / 60000;

            const newTotalBreakTime = (activeSession.totalBreakTime || 0) + duration;
            const breaksTaken = (activeSession.breaksTaken || 0) + 1;
            const limitReached = newTotalBreakTime >= 60;

            await Promise.all([
                endBreak(activeBreak.$id, userClickTime, duration),
                databases.updateDocument(
                    dbId,
                    sessionsCollectionId,
                    activeSession.$id,
                    {
                        status: "active",
                        totalBreakTime: newTotalBreakTime,
                        breaksTaken: breaksTaken,
                        breakLimitReached: limitReached,
                        updatedAt: userClickTime.toISOString()
                    }
                )
            ]);

            stopBreakTimer();
            setBreakElapsedTime(0);

            toast.success('Break finalizado correctamente.');

        } catch (error) {
            console.error("Error finalizando break", error);
            toast.error("Error al finalizar el break.");
        } finally {
            breakInFlightRef.current = false;
            setIsBreakLoading(false);
        }
    };

    // Calcular breaks finalizados desde realtime
    const finishedBreaks = useMemo(() => {
        return realtimeBreaks.filter((br: any) => !br.isActive);
    }, [realtimeBreaks]);

    // Calcular tiempo total de breaks (memoizado)
    const totalBreakTime = useMemo(() => {
        const finishedTime = finishedBreaks.reduce((acc: number, br: any) => {
            return acc + ((br.breakDuration || 0) * 60 * 1000);
        }, 0);

        const currentBreakTime = activeBreak && breakStartTimeRef.current
            ? breakElapsedTime
            : 0;

        return finishedTime + currentBreakTime;
    }, [finishedBreaks, activeBreak, breakElapsedTime]);

    const handleSendNote = async () => {
        if (!user || !noteText.trim()) return;

        if (!isOnline) {
            toast.error("Sin conexión a internet. No se puede enviar la nota.");
            return;
        }

        if (!activeSession) {
            toast.error("Debes iniciar la jornada antes de enviar una nota.");
            return;
        }

        try {
            setIsSendingNote(true);
            await createSessionNote(user.$id, activeSession.$id, noteText.trim());
            setNoteText("");
            toast.success("Nota enviada correctamente.");
        } catch (error) {
            console.error("Error enviando nota", error);
            toast.error("Hubo un error al enviar la nota.");
        } finally {
            setIsSendingNote(false);
        }
    };

    //tamaño boton
    const BUTTON_SIZE_CLASS = "w-60 h-14";
    const ICON_SIZE_CLASS = "!w-6 !h-6 text-white "; // Tamaño estándar para iconos
    const TEXT_SIZE_CLASS = "text-base rounded-full"; // Tamaño estándar para texto
    const ICON_MARGIN_CLASS = "mr-2"; // Margen estándar entre icono


    const renderMainButton = () => {
        // Si esta cargando la informacion inicial, mostrara estado de carga
        if (isInitialDataLoading) {
            return (
                <Button
                    disabled
                    // hidden
                    className={`bg-gray-400 text-white cursor-not-allowed ${BUTTON_SIZE_CLASS} ${TEXT_SIZE_CLASS}`}

                >
                    {/* <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Cargando... */}
                    <Loader className={` ${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS}`} /> Cargando...
                </Button>
            );
        }

        // Si no hay sesión activa
        if (!activeSession) {
            return (
                <>
                    <Button
                        onClick={handleStartWork}
                        className={`bg-(--blue-wist-2) hover:bg-blue-600 text-white cursor-pointer ${BUTTON_SIZE_CLASS} ${TEXT_SIZE_CLASS}`}
                        disabled={isLoading || hasSessionToday}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className={`${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS} animate-spin`} />
                                Iniciando...
                            </>
                        ) : (
                            <>
                                <Play className={` fill-white ${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS}`} /> Iniciar Jornada
                            </>
                        )}
                    </Button>

                    {hasSessionToday && !isLoading && (
                        <div className="flex  py-3 ">
                            <CircleAlert className="text-orange-300 w-3 h-3  md:mt-0 " />
                            <p className="text-xs text-orange-300 font-outfit">
                                Ya registraste una jornada hoy.  Mañana podrás iniciar otra 🚀.
                            </p>
                        </div>
                    )}
                </>
            );
        }

        // Si hay sesión activa
        return (
            <AlertDialog>
                <AlertDialogTrigger asChild>
                    <Button
                        className={`bg-(--red-wist) text-gray-50 text-lg cursor-pointer hover:bg-red-600  ${BUTTON_SIZE_CLASS} ${TEXT_SIZE_CLASS}`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className={`${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS} animate-spin`} />
                                Finalizando...
                            </>
                        ) : (
                            <>
                                <DoorOpen className={`${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS}`} />
                                Finalizar Jornada
                            </>
                        )}
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <div className="flex items-center gap-2">
                            <DoorOpen className="w-6 h-6 text-red-500" />
                            <AlertDialogTitle className="font-outfit text-red-500">Finalizar tu jornada</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-600 font-outfit dark:text-gray-400">
                            Esta acción finalizará tu jornada laboral del día y registrará tu hora de salida.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleEndWork}
                            className="bg-red-500 hover:bg-red-600 dark:text-white cursor-pointer"
                        >
                            Sí, finalizar jornada
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        );
    };

    // Función para renderizar el botón de break con estados mejorados
    const renderBreakButton = () => {
        // Si no hay datos cargados o no hay sesión, deshabilitar
        if (isInitialDataLoading || !activeSession) {
            return (
                <Button
                    // disabled
                    hidden
                    className={`bg-gray-400 text-white cursor-not-allowed opacity-50 ${BUTTON_SIZE_CLASS} ${TEXT_SIZE_CLASS}`}
                >
                    <Clock2 className={`${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS}`} />
                    {isInitialDataLoading ? 'Cargando...' : 'Inicia jornada primero'}
                </Button>
            );
        }

        if (activeBreak) {
            return (
                <Button
                    onClick={handleEndBreak}
                    variant="default"
                    className={`bg-yellow-500 text-gray-50 cursor-pointer hover:bg-yellow-600 ${BUTTON_SIZE_CLASS} ${TEXT_SIZE_CLASS}`}
                    disabled={isBreakLoading}
                >
                    {isBreakLoading ? (
                        <>
                            <Loader2 className={`${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS} animate-spin`} />
                            Pausando...
                        </>
                    ) : (
                        <>
                            <Pause className={`fill-white ${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS}`} /> Parar Break
                        </>
                    )}
                </Button>
            );
        }

        return (
            <Button
                onClick={handleStartBreak}
                className={`bg-[#83bb26] hover:bg-[#6ea71f] text-gray-50 cursor-pointer ${breakLimitReached ? 'opacity-50 cursor-not-allowed' : ''} ${BUTTON_SIZE_CLASS} ${TEXT_SIZE_CLASS}`}
                disabled={isBreakLoading || breakLimitReached}
            >
                {isBreakLoading ? (
                    <>
                        <Loader2 className={`${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS} animate-spin`} />
                        Iniciando...
                    </>
                ) : (
                    <>
                        <Play className={`fill-white ${ICON_SIZE_CLASS} ${ICON_MARGIN_CLASS}`} /> Iniciar Break
                    </>
                )}
            </Button>
        );
    };

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <Home className="h-4 w-4" />
                        <BreadcrumbPage>Inicio</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="px-3 max-w-7xl mx-auto">

                <div className="py-6">
                    <h1 className="font-outfit text-xl font-light">
                        Bienvenido de vuelta <span style={{ color: nameColor }}>{user?.name}</span>
                    </h1>
                </div>

                {/* 
                <div className="py-6">
                    <Card className="text-center w-full max-w-md mx-auto shadow-sm">
                        <CardContent>
                            <h1 className="font-outfit text-2xl">
                                Hola, bienvenido al sistema <span style={{ color: nameColor }}>{user?.name}</span>
                            </h1>
                        </CardContent>
                    </Card>
                </div> */}


                {/* componente frase del dia */}
                <div className="pb-4">
                    <PhraseOfTheDay />
                </div>

                {/* FAB flotante de eventos del dia — oculto hasta que esté listo */}
                <DashboardEvents />

                {/* div antiguo para mostrar el orden de las card */}

                {/* <div className={`flex flex-col gap-4 mb-6 text-center ${!isInitialDataLoading && activeSession ? 'md:flex-row' : 'md:flex-row md:justify-center'}`}> */}

                <div
                    key={activeSession ? 'with-session' : 'no-session'}
                    className={`flex flex-col gap-4 mb-6 text-center ${!isInitialDataLoading && activeSession ? 'md:flex-row' : 'md:flex-row md:justify-center'}`}
                >
                    {!isInitialDataLoading && activeSession ? (
                        <>
                            {/* Card Break  */}
                            <Card className="w-full md:w-1/2 text-center items-center shadow-sm hover:shadow-xl rounded-3xl ">
                                <CardContent className="flex flex-col gap-3 font-outfit">
                                    <h1 className="text-xl text-shadow-black">Control del Break</h1>
                                    <h2 className="text-sm text-gray-400 dark:text-gray-500">Tiempo Acumulado</h2>
                                    <div className="flex gap-3 justify-center">
                                        <Coffee className="w-4 h-4 text-gray-400 mt-1.5" />
                                        <h1 className="text-gray-500 text-lg dark:text-gray-400">
                                            {formatTime(totalBreakTime)}
                                        </h1>
                                    </div>
                                    {renderBreakButton()}
                                </CardContent>
                                {breakLimitReached && (
                                    <div className="flex py-3">
                                        <CircleAlert className="text-orange-300 w-3 h-3  md:mt-0" />
                                        <p className="text-xs text-orange-300 font-outfit">
                                            Has alcanzado el límite de tiempo de break para hoy 😄.
                                        </p>
                                    </div>
                                )}
                            </Card>

                            {/* Card Asistencia  */}
                            <Card className="w-full md:w-1/2 rounded-3xl text-center items-center shadow-sm hover:shadow-lg bg-linear-to-r dark:from-zinc-800 dark:to-zinc-900">
                                <CardContent className="flex flex-col gap-3 font-outfit">
                                    <h1 className="text-xl text-shadow-black">Control de Asistencia</h1>
                                    <h2 className="text-sm text-gray-400 dark:text-gray-500 ">Hora Actual</h2>
                                    <div className="flex gap-2 justify-center ">
                                        <Clock2 className="w-4 h-4 text-gray-400 mt-1.5" />
                                        <h1 className=" text-gray-500 text-lg dark:text-gray-400">
                                            {ecuadorTime || "Cargando..."}
                                        </h1>
                                    </div>
                                    <div className="justify-center">
                                        {renderMainButton()}
                                    </div>
                                </CardContent>
                                <Sheet>
                                    <SheetTrigger asChild className="cursor-pointer">
                                        <Button
                                            className=" font-outfit border dark:border-gray-400 text-gray-500 dark:text-gray-400"
                                            variant="outline"
                                            disabled={isInitialDataLoading}
                                        >
                                            <NotebookPen className="text-gray-500 dark:text-gray-400" /> Notas
                                        </Button>
                                    </SheetTrigger>
                                    <SheetContent>
                                        <SheetHeader>
                                            <SheetTitle className="text-center">Notas de la sesión</SheetTitle>
                                            <SheetDescription>
                                                En este apartado podras proporcionar el motivo o nota acerca del control de  asistencia.
                                            </SheetDescription>
                                        </SheetHeader>
                                        <div className="grid flex-1 auto-rows-min gap-6 px-4">
                                            <div className="grid gap-3">
                                                <Label htmlFor="sheet-demo-username">Nota</Label>
                                                <Textarea
                                                    placeholder="Ingresa el motivo aqui..."
                                                    value={noteText}
                                                    onChange={(e) => setNoteText(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <SheetFooter>
                                            <Button
                                                type="button"
                                                className="cursor-pointer"
                                                disabled={isSendingNote || !noteText.trim()}
                                                onClick={handleSendNote}
                                            >
                                                {isSendingNote ? "Enviando..." : "Enviar Nota"}
                                            </Button>
                                            <SheetClose asChild>
                                                <Button className="cursor-pointer" variant="outline">Cancelar</Button>
                                            </SheetClose>
                                        </SheetFooter>
                                    </SheetContent>
                                </Sheet>
                            </Card>
                        </>
                    ) : (
                        /* Solo Card Asistencia cuando NO hay sesión */
                        <Card className="w-full md:w-1/2 rounded-3xl text-center items-center shadow-sm hover:shadow-lg bg-linear-to-r dark:from-zinc-800 dark:to-zinc-900">
                            <CardContent className="flex flex-col gap-3 font-outfit">
                                <h1 className="text-xl text-shadow-black">Control de Asistencia</h1>
                                <h2 className="text-sm text-gray-400 dark:text-gray-500 ">Hora Actual</h2>
                                <div className="flex gap-2 justify-center ">
                                    <Clock2 className="w-4 h-4 text-gray-400 mt-1.5" />
                                    <h1 className=" text-gray-500 text-lg dark:text-gray-400">
                                        {ecuadorTime || "Cargando..."}
                                    </h1>
                                </div>
                                <div className="justify-center">
                                    {renderMainButton()}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
            <Feedback />

            {/* Botón de recarga — solo iOS standalone, solo mobile */}
            {typeof window !== "undefined" && (window.navigator as { standalone?: boolean }).standalone === true && (
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="fixed bottom-24 left-4 z-50 flex items-center justify-center w-9 h-9 rounded-full bg-background/60 backdrop-blur-sm border border-border/40 text-muted-foreground shadow-sm hover:bg-background/80 hover:text-foreground transition-all md:hidden"
                    aria-label="Recargar página"
                >
                    <RefreshCw className="w-4 h-4" />
                </button>
            )}
        </>

    );
};

export default Dashboard;